import React, { Component } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { FormattedMessage, defineMessages, injectIntl } from "react-intl";

import {
  TextField,
  Loading,
  COLORS,
  BREAKPOINTS
} from "@onelogin/react-components";
import {
  NEW_NOTE_SCREEN,
  PUBLIC_URL,
  SHOW_NOTE_SCREEN
} from "../../constants/Env";
import {
  fetchCurrentNote,
  submitNewNote,
  updateCurrentNote,
  setScreenToShowNote,
  setScreenToNotesList
} from "../../actions/notesActions";
import { notifyError } from "../../actions/notificationsActions";
import { fetchCurrentNoteGrants } from "../../actions/grantsActions";
import ShowNoteHeader from "./ShowNoteHeader";
import ShowNoteFooter from "./ShowNoteFooter";
import CancelEditingWarning from "./CancelEditingWarning";
import ContentEditable from "./ContentEditable";

const COLUMN_FONT_SIZE = 18;
const COLUMN_LINE_HEIGHT = 24;

const messages = defineMessages({
  untitled: {
    id: "untitled",
    defaultMessage: "Untitled"
  },
  contentPlaceholder: {
    id: "contentPlaceholder",
    defaultMessage: "Start writing your secure note"
  },
  emptyContent: {
    id: "emptyContent",
    defaultMessage: "There is no content for this note"
  }
});

const SecureNoteContainer = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.WHITE};
`;

const SecureNoteRefContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: calc(100% - 56px);
`;

const SecureNoteTitleContainer = styled.div`
  flex: 0 0 38px;
  background-color: ${COLORS.WHITE};

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    background-color: transparent;
  }
`;

const SecureNoteTitleWrapper = styled.div`
  vertical-align: middle;
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    flex-direction: row-reverse;
  }
`;

const SecureNoteTitle = styled.div`
  height: 100%;
  font-size: 26px;
  padding: 2px 12px;
  flex-grow: 1;
`;

const TitleTextField = styled(TextField)`
  font-size: ${COLUMN_FONT_SIZE}px;
  font-weight: 500;
  height: 100%;
  padding: 6px 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  font-stretch: normal;
  font-style: normal;
  line-height: ${COLUMN_LINE_HEIGHT}px;
  letter-spacing: normal;
  color: #3f4040;
  border: 1px solid rgb(222, 224, 224);


  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    padding: 6px 12px;
  }

  :empty::before {
    content: "${props => props.placeholder}";
    color: ${COLORS.GRAY_COLORS.GRAY_45};
    font-style: italic;
  }
`;

const ContentContainer = styled.div`
  background-color: ${COLORS.WHITE};
  overflow-y: auto;
  display: flex;
  flex-grow: 1;
  padding: 10px 12px;
`;

const ContentTextField = styled(TextField)`
  padding: 6px 12px; 
  width: 100%;
  height: fit-content;
  min-height: calc(
    100% - 6px * 2 - 6px * 2
  ); // minus top and bottom paddings and margins
  border: 1px solid rgb(222, 224, 224);
  :empty::before {
    content: "${props => props.placeholder}";
    color: ${COLORS.GRAY_COLORS.GRAY_45};
    font-style: italic;
`;

class ShowNoteScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      renderCancelWarning: false,
      title: "",
      content: "",
      isContentVisible: true
    };
    this.screenRef = React.createRef();
  }

  componentDidUpdate = prevProps => {
    if (
      prevProps.screen !== NEW_NOTE_SCREEN &&
      this.props.screen === NEW_NOTE_SCREEN
    ) {
      this.resetTitleContentState();
    } else {
      if (
        this.props.currentNote &&
        prevProps.currentNote !== this.props.currentNote
      ) {
        this.setState({
          title: this.props.currentNote.title,
          content: this.props.currentNote.content,
          isContentVisible: false
        });
      }
    }
  };

  componentWillUnmount = () => {
    this.resetTitleContentState();
    this.setMouseDownHandler(false);
  };

  resetTitleContentState = () => {
    this.setState({
      title: "",
      content: "",
      isContentVisible: true
    });
  };

  onToggleMaskContent = () => {
    this.setState({
      isContentVisible: !this.state.isContentVisible
    });
  };

  goBackToNotesList = () => {
    this.resetTitleContentState();
    this.props.setScreenToNotesList();
    this.props.history.push(`${PUBLIC_URL}`);
  };

  onClickShare = id => {
    this.props.history.push(`${PUBLIC_URL}/share/${id}`);
  };

  onCancelEditNote = () => {
    this.props.setScreenToShowNote();
    this.props.history.push(`${PUBLIC_URL}/${this.props.currentNote.id}`);
  };

  onSaveNote = () => {
    if (!this.state.title.trim() || !this.state.content.trim()) {
      this.props.showNoTitleContentErrorNotification();
    } else {
      this.setMouseDownHandler(false);
      if (this.props.screen === SHOW_NOTE_SCREEN) {
        this.props.updateCurrentNote({
          id: this.props.currentNote.id,
          title: this.state.title.trim(),
          content: this.state.content.trim()
        });
      } else {
        this.props.submitNewNote({
          title: this.state.title.trim(),
          content: this.state.content.trim(),
          history: this.props.history
        });
      }
    }
  };

  onClickCancelEditing = () => {
    if (!this.props.currentNote) {
      if (this.state.title.trim() || this.state.content.trim()) {
        this.setState({
          renderCancelWarning: true
        });
      } else {
        this.goBackToNotesList();
      }
    } else {
      if (
        this.state.title.trim() !== this.props.currentNote.title.trim() ||
        this.state.content.trim() !== this.props.currentNote.content.trim()
      ) {
        this.setState({
          renderCancelWarning: true
        });
      }
    }
  };

  onCancelEditingWarning = () => {
    this.setState({
      renderCancelWarning: false
    });
  };

  onProceedToCancelEditing = () => {
    this.setState({
      title: this.props.currentNote.title,
      content: this.props.currentNote.content
    });
    this.setMouseDownHandler(false);
    this.onCancelEditingWarning();
    this.onCancelEditNote();
  };

  setMouseDownHandler = enabled => {
    if (enabled) {
      document.addEventListener("mousedown", this.handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", this.handleOutsideClick);
    }
  };

  handleOutsideClick = event => {
    event.stopPropagation();

    if (this.screenRef && !this.screenRef.current.contains(event.target)) {
      this.onClickCancelEditing();
    }
  };

  onChange = (key, value) => {
    this.setState({
      [key]: value
    });
  };

  saveButtonDisabled = () => {
    if (this.props.screen === SHOW_NOTE_SCREEN) {
      return (
        (this.state.title.trim() === this.props.currentNote.title.trim() &&
          this.state.content.trim() ===
            this.props.currentNote.content.trim()) ||
        !this.state.title.trim() ||
        !this.state.content.trim()
      );
    } else if (this.props.screen === NEW_NOTE_SCREEN) {
      return !this.state.title.trim() || !this.state.content.trim();
    }
  };

  render() {
    const { intl, currentNote, loading, screen, onClickRemove } = this.props;
    const isReadOnly = currentNote && !currentNote.write_access;
    const isOwner = currentNote && currentNote.user_is_owner;
    const isEditor = currentNote && currentNote.write_access;

    if (loading || (!currentNote && screen === SHOW_NOTE_SCREEN)) {
      return (
        <SecureNoteContainer>
          <Loading />
        </SecureNoteContainer>
      );
    }

    return (
      <SecureNoteContainer>
        <ShowNoteHeader
          currentNote={currentNote}
          isOwner={isOwner}
          isViewer={isReadOnly}
          isEditor={isEditor}
          goBackToNotesList={this.goBackToNotesList}
          onClickShare={this.onClickShare}
          onClickRemove={onClickRemove}
          onToggleMaskContent={this.onToggleMaskContent}
          isContentVisible={this.state.isContentVisible}
        />
        <SecureNoteRefContainer ref={this.screenRef}>
          <SecureNoteTitleContainer>
            <SecureNoteTitleWrapper>
              <SecureNoteTitle
                onClick={() => this.setMouseDownHandler(true)}
                data-testid="title-input"
              >
                <ContentEditable
                  value={this.state.title}
                  node={TitleTextField}
                  placeholder={intl.formatMessage(messages.untitled)}
                  onChange={(event, value) => this.onChange("title", value)}
                  editable={!isReadOnly || isEditor}
                  autoFocus={screen === NEW_NOTE_SCREEN}
                />
              </SecureNoteTitle>
            </SecureNoteTitleWrapper>
          </SecureNoteTitleContainer>
          <ContentContainer
            onClick={() => this.setMouseDownHandler(true)}
            data-testid="content-input"
          >
            <ContentEditable
              value={
                this.state.isContentVisible
                  ? this.state.content
                  : this.state.content.replace(/[\s\S]/g, "\u25CF")
              }
              node={ContentTextField}
              placeholder={
                isReadOnly
                  ? intl.formatMessage(messages.emptyContent)
                  : intl.formatMessage(messages.contentPlaceholder)
              }
              onChange={(event, value) => this.onChange("content", value)}
              editable={
                (!isReadOnly || isEditor) && this.state.isContentVisible
              }
            />
          </ContentContainer>
          <ShowNoteFooter
            currentNote={currentNote}
            hasWriteAccess={!isReadOnly}
            goBackToNotesList={this.goBackToNotesList}
            screen={screen}
            onSaveNote={this.onSaveNote}
            saveButtonDisabled={this.saveButtonDisabled()}
          />
        </SecureNoteRefContainer>
        {this.state.renderCancelWarning && (
          <CancelEditingWarning
            currentNote={currentNote}
            goBackToNotesList={this.goBackToNotesList}
            onCancelEditingWarning={this.onCancelEditingWarning}
            onProceedToCancelEditing={this.onProceedToCancelEditing}
          />
        )}
      </SecureNoteContainer>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  intl: ownProps.intl,
  currentNote: state.notes.currentNote,
  loading: state.notes.loading,
  screen: state.notes.screen
});

const mapDispatchToProps = dispatch => ({
  fetchCurrentNote: id => {
    dispatch(fetchCurrentNote(id));
  },
  submitNewNote: ({ title, content, history }) => {
    dispatch(submitNewNote({ title, content, history }));
  },
  fetchCurrentNoteGrants: id => {
    dispatch(fetchCurrentNoteGrants(id));
  },
  updateCurrentNote: ({ id, title, content }) => {
    dispatch(updateCurrentNote({ id, title, content }));
  },
  setScreenToShowNote: () => {
    dispatch(setScreenToShowNote());
  },
  setScreenToNotesList: () => {
    dispatch(setScreenToNotesList());
  },
  showNoTitleContentErrorNotification: () => {
    dispatch(
      notifyError({
        message: (
          <FormattedMessage
            defaultMessage="Please enter title and content before submitting the secure note"
            id="noTitleContentErrorNotification"
          />
        )
      })
    );
  }
});

ShowNoteScreen.propTypes = {
  currentNote: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired
  }),
  loading: PropTypes.bool.isRequired,
  screen: PropTypes.string.isRequired,
  onClickRemove: PropTypes.func.isRequired,
  fetchCurrentNote: PropTypes.func.isRequired,
  submitNewNote: PropTypes.func.isRequired,
  fetchCurrentNoteGrants: PropTypes.func.isRequired,
  updateCurrentNote: PropTypes.func.isRequired,
  setScreenToShowNote: PropTypes.func.isRequired,
  setScreenToNotesList: PropTypes.func.isRequired
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(ShowNoteScreen)
  )
);
